'use client';

import * as React from 'react';

import * as ToggleSwitchPrimitive from '@radix-ui/react-switch';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

export const toggleSwitchVariants = tv({
  base: 'h-6 w-10 border border-gray-300 rounded-full overflow-hidden',
  variants: {
    variant: {
      default:
        'data-[state=checked]:bg-primary-100 data-[state=checked]:border-primary-300 disabled:data-[state=checked]:bg-gray-100 disabled:data-[state=checked]:border-gray-300 data-[readonly=true]:cursor-not-allowed data-[readonly=true]:pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type ToggleSwitchProps = ToggleSwitchPrimitive.SwitchProps & {
  isReadOnly?: boolean;
  dataTestId?: string;
} & VariantProps<typeof toggleSwitchVariants>;

export const ToggleSwitch = React.forwardRef<
  React.ElementRef<typeof ToggleSwitchPrimitive.Root>,
  ToggleSwitchProps
>((props, ref) => {
  const [isChecked, setIsChecked] = React.useState(props.defaultChecked);

  React.useEffect(() => {
    setIsChecked(props.defaultChecked);
  }, [props.defaultChecked]);

  return (
    <ToggleSwitchPrimitive.Root
      className={cn(
        toggleSwitchVariants({
          variant: props.variant,
          className: props.className,
        }),
      )}
      checked={isChecked}
      data-readonly={props.isReadOnly}
      data-testid={props.dataTestId}
      onClick={() => {
        setIsChecked((o) => !o);
      }}
      ref={ref}
      {...props}
    >
      <ToggleSwitchPrimitive.Thumb
        className={cn(
          'shadow-switch ml-1 block h-4 w-4 cursor-pointer rounded-full bg-gray-400 transition-transform duration-100 will-change-transform disabled:cursor-none data-[state=checked]:translate-x-3',
          {
            'data-[state=checked]:bg-primary-500': !props.disabled,
            'data-[state=checked]:bg-gray-400': props.disabled,
          },
        )}
      />
    </ToggleSwitchPrimitive.Root>
  );
});

ToggleSwitch.displayName = 'ToggleSwitch';
